import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Paper,
    Box,
    Typography,
    TableSortLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RewardsFilterPopup from "../RewardsPointsBook/RewardsFilterPopup";
import PaginationComponent from "../../brands/Pagination";
import ReportAllFilter from "./ReportAccountFilter";
import DropDownFilter, { Option } from "./DropDownFilter";
import Export from "../../../../src/assests/images/export.svg";
import Sorting from "../../../../src/assests/images/Sorting.svg";


// Column dropdown options
const columns: Option[] = [
    { label: "Account ID", value: "account_id" },
    { label: "PMI Entity Code", value: "Pmi_entity_code" },
    { label: "Outlet Name", value: "outlet_name" },
    { label: "City", value: "city" },
    { label: "Outlet Type", value: "outlet_type" },
];

// Interfaces
export interface RewardPointResponse {

    account_id: number;
    Pmi_entity_code: string;
    outlet_name: string;
    city: string;
    outlet_type: string;
}

const mockUsers: RewardPointResponse[] = [
    {

        account_id: 1,
        Pmi_entity_code: "IN-DTETraining_IN6",
        outlet_name: "DTE Shop 6 India Training ",
        city: "Delhi",
        outlet_type: "Marlboro",
    },
    {
        account_id: 2,
        Pmi_entity_code: "IN-DTETraining_IN7",
        outlet_name: "DTE Shop 7 India Training",
        city: "Delhi",
        outlet_type: "Marlboro",
    },
    {

        account_id: 1,
        Pmi_entity_code: "DTE Shop 6 India Training",
        outlet_name: "DTE Shop 8 India Training",
        city: "Delhi",
        outlet_type: "Marlboro",
    },
];

const RewardsAccountTable = () => {
    const [users, setUsers] = useState<RewardPointResponse[]>(mockUsers);
    const [page, setPage] = useState(1);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const [selectedCols, setSelectedCols] = useState<string[]>([]);
    const [orderBy, setOrderBy] = useState<keyof RewardPointResponse>("account_id");
    const [order, setOrder] = useState<"asc" | "desc">("asc");

    // Pagination
    const itemsPerPage = 10;
    const totalPages = Math.ceil(users.length / itemsPerPage);

    // Sorting logic
    const handleSort = (property: keyof RewardPointResponse) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const sortedUsers = [...users].sort((a, b) => {
        if (a[orderBy] < b[orderBy]) return order === "asc" ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return order === "asc" ? 1 : -1;
        return 0;
    });

    const paginatedUsers = sortedUsers.slice(
        (page - 1) * itemsPerPage,
        page * itemsPerPage
    );

    return (
        <Box sx={{ background: "#fff", borderRadius: "10px", border: "1px solid #cfcfcf" }}>
            {/* Header with filters */}
            <Box
                sx={{
                    padding: "25px ",
                    display: "flex",
                    justifyContent: "space-between",
                }}
            >
                <Typography sx={{ fontSize: "16px", fontWeight: "600", marginTop: "10px" }}>
                    Total {users.length} Users
                </Typography>
                <Box sx={{ display: "flex" }}>
                    <Box sx={{ width: "200px", marginRight: "20px" }}>
                        <DropDownFilter
                            placeholder="Select columns"
                            options={columns}
                            value={selectedCols}
                            onChange={setSelectedCols}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        onClick={() => setIsPopupVisible((prev) => !prev)}
                        sx={{
                            textTransform: "capitalize",
                            backgroundColor: "#fff",
                            height: "44px",
                            padding: "7px 12px",
                            borderRadius: "4px",
                            color: "#565656",
                            border: "1px solid #DEDEDE",
                            boxShadow: "none",
                            marginRight: "20px",
                            "&:hover": { backgroundColor: "#fff" },
                        }}
                        endIcon={<ExpandMoreIcon sx={{ fontSize: "30px" }} />}
                    >
                        Filters
                    </Button>
                    {isPopupVisible && (
                        <div ref={popupRef}>
                            <ReportAllFilter />
                        </div>
                    )}
                    <Button
                        variant="contained"
                        sx={{
                            textTransform: "capitalize",
                            backgroundColor: "#fff",
                            fontSize: "14px",
                            fontWeight: "500",
                            height: "40px",
                            padding: "16px",
                            borderRadius: "10px",
                            color: "#C8172F",
                            border: "1px solid #C8172F",
                            boxShadow: "none",
                        }}
                        startIcon={<img src={Export} alt="export" />}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
                <Table>
                    <TableHead sx={{ background: "#EEF4FF" }}>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell key={col.value}>
                                    <TableSortLabel
                                        active={orderBy === col.value}
                                        direction={orderBy === col.value ? order : "asc"}
                                        onClick={() => handleSort(col.value as keyof RewardPointResponse)}
                                        IconComponent={() => (
                                            <img
                                                src={order === "asc" ? Sorting : Sorting}
                                                alt="sort"
                                                style={{ width: 16, height: 16 }}
                                            />
                                        )}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedUsers.map((user) => (
                            <TableRow>
                                <TableCell>{user.account_id}</TableCell>
                                <TableCell>{user.Pmi_entity_code}</TableCell>
                                <TableCell>{user.outlet_name}</TableCell>
                                <TableCell>{user.city}</TableCell>
                                <TableCell>{user.outlet_type}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <PaginationComponent
                page={page}
                limit={itemsPerPage}
                totalPages={totalPages}
                setPage={setPage}
            />
        </Box>
    );
};

export default RewardsAccountTable;
